import useAuth from '@/hooks/stores/useAuth';
import { fieldConditionsService, userService, formService } from '@/services';
import React, { useState, useEffect, useRef } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PlayCircle, AlertCircle, Menu } from 'lucide-react';

const UserSubmissionView = () => {
  const { userId, submissionId } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [activeTab, setActiveTab] = useState('form');
  const [formValues, setFormValues] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [formName, setFormName] = useState('');
  const [formData, setFormData] = useState(null);
  const mainContentRef = useRef(null);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [error, setError] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [skippedFields, setSkippedFields] = useState({});

  const fetchFormData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('Starting fetchFormData...', { userId, submissionId });

      if (!userId || !submissionId) {
        throw new Error('Missing required parameters: userId or submissionId');
      }

      const userResponse = await userService.getUserData(userId);
      console.log('User data response:', userResponse);

      if (!userResponse?.success) {
        throw new Error(userResponse?.message || 'Failed to fetch user submission data');
      }

      const submission = userResponse.data.form_submissions.find(
        sub => sub.id === parseInt(submissionId)
      );
      console.log('Found submission:', submission);

      if (!submission) {
        throw new Error(`No submission found with ID: ${submissionId}`);
      }

      const formId = submission.form.id;
      const formResponse = await formService.getForm(formId);

      if (!formResponse?.data) {
        throw new Error(formResponse?.message || 'Failed to fetch form data');
      }

      const form = formResponse.data;
      setFormData(form);
      setFormName(submission.form.name || form.name);

      const processedValues = {};
      const newSkippedFields = {};

      if (Array.isArray(submission.submission_values)) {
        submission.submission_values.forEach(submission => {
          if (!processedValues[submission.section]) {
            processedValues[submission.section] = [];
          }
          
          while (processedValues[submission.section].length <= submission.section_repeat_index) {
            processedValues[submission.section].push({});
          }
          
          // Track skipped fields
          if (submission.skip === 1) {
            const key = `${submission.section}_${submission.field_id}_${submission.section_repeat_index || 0}`;
            newSkippedFields[key] = true;
          }
          
          // Special handling for file fields
          if (typeof submission.value === 'string' && submission.value.includes('file_id')) {
            try {
              const fileData = JSON.parse(submission.value);
              processedValues[submission.section][submission.section_repeat_index][submission.field_id] = fileData;
            } catch (e) {
              console.error('Error parsing file data:', e);
              processedValues[submission.section][submission.section_repeat_index][submission.field_id] = submission.value;
            }
          } else {
            processedValues[submission.section][submission.section_repeat_index][submission.field_id] = submission.value;
          }
        });
      }
      
      setSkippedFields(newSkippedFields);
      setFormValues(processedValues);
      
      const allSteps = new Set(form.form_form_steps.map((_, index) => index));
      setCompletedSteps(allSteps);

    } catch (error) {
      console.error('Error in fetchFormData:', error);
      setError(error.message || 'An unexpected error occurred while fetching the form data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFormData();
  }, [userId, submissionId]);

  const scrollToTop = () => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  };

  useEffect(() => {
    scrollToTop();
  }, [currentStep, activeTab]);

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-auto text-center p-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-6">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Form</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Go Back
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 text-sm font-medium text-white bg-[#F16745] rounded-md hover:bg-[#F16745]"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden">
      <Toaster 
        toastOptions={{
          style: {
            marginTop: '60px',
          },
        }}
      />
      {isLoading ? (
        <div className="flex items-center justify-center h-full w-full">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F16745]"></div>
            <p className="mt-4 text-gray-600">Loading form data...</p>
          </div>
        </div>
      ) : (
        <div className="flex w-full h-full">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden fixed top-4 right-4 z-50 p-2 rounded-md text-[#F16745] hover:text-[#E55D3D] hover:bg-accent/10 transition-colors"
            aria-label="Toggle menu"
          >
            <Menu size={24} />
          </button>

          <aside className={`
            fixed lg:static
            w-80 bg-white shadow-xl h-full flex-shrink-0 overflow-y-auto
            transition-transform duration-300 ease-in-out
            ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
            z-40 lg:z-auto
            right-0 lg:left-0 lg:ml-0
            border-l lg:border-0
          `}>
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">{formName || 'Booklet'}</h2>
              <nav>
                <ul className="space-y-2">
                  {formData?.form_form_steps.map((step, index) => (
                    <li key={step.id}>
                      <button
                        onClick={() => {
                          setCurrentStep(index);
                          scrollToTop();
                        }}
                        className={`w-full flex items-center px-4 py-3 rounded-lg text-sm transition-colors ${
                          index === currentStep
                            ? 'bg-blue-50 text-[#F16745] ring-2 ring-[#F16745] ring-opacity-50'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <span className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full mr-3 text-sm mt-0.5
                          ${index === currentStep
                            ? 'bg-blue-100 text-[#F16745] border-2 border-[#F16745]'
                            : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="font-medium leading-tight">{step.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </aside>

          <main ref={mainContentRef} className="flex-1 overflow-y-auto bg-gray-50">
            <div className="p-4 lg:p-8">
              <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm p-8">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {formData?.form_form_steps[currentStep]?.title}
                  </h2>
                  <div className="text-sm text-gray-500">
                    Viewing submission for User ID: {userId}
                  </div>
                </div>

                {formData?.form_form_steps[currentStep]?.form_step_form_sections.map((section) => (
                  <div key={section.id} className="mb-10">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">{section.name}</h3>

                    {(formValues[section.id]?.length > 0 ? formValues[section.id] : [{}]).map((rowInstance, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg mb-6">
                        <div className="grid grid-cols-1 gap-6 p-6">
                          {section.form_step_form_fields.map((field) => (
                            <div key={field.id} className="space-y-2">
                              {field.field_type === 'heading' ? (
                                <h4 className="text-lg font-semibold text-gray-900">
                                  {field.label || field.name}
                                </h4>
                              ) : (
                                <>
                                  <label className="block text-sm font-medium text-gray-700">
                                    {field.name}
                                  </label>
                                  <div className="mt-1">
                                    {skippedFields[`${section.id}_${field.id}_${index}`] ? (
                                      <div className="px-4 py-3 bg-white rounded-lg border border-gray-200">
                                        <div className="text-gray-600 italic">
                                          This question has been skipped
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="px-4 py-3 bg-white rounded-lg border border-gray-200">
                                        {field.field_type === 'checkbox' ? (
                                          <div className="flex items-center">
                                            <input
                                              type="checkbox"
                                              checked={rowInstance?.[field.id] === "1"}
                                              disabled
                                              className="h-5 w-5 rounded border-gray-300 text-[#F16745]"
                                            />
                                            <span className="ml-3 text-sm text-gray-600">
                                              {rowInstance?.[field.id] === "1" ? "Yes" : "No"}
                                            </span>
                                          </div>
                                        ) : field.field_type === 'radio' ? (
                                          <div className="text-gray-900">
                                            {field.options && JSON.parse(field.options).find(opt => opt.value === rowInstance?.[field.id])?.label || rowInstance?.[field.id] || 'Not provided'}
                                          </div>
                                        ) : field.field_type === 'file' ? (
                                          <div className="flex items-center justify-between">
                                            <div className="text-gray-900">
                                              {rowInstance?.[field.id] ? (
                                                typeof rowInstance[field.id] === 'object' ? 
                                                  rowInstance[field.id].name : 
                                                  rowInstance[field.id].split('/').pop().replace(/^\d+_/, '')
                                              ) : 'Not provided'}
                                            </div>
                                            {rowInstance?.[field.id] && (
                                              <button
                                                onClick={() => window.open(
                                                  typeof rowInstance[field.id] === 'object' ? 
                                                    rowInstance[field.id].url : 
                                                    rowInstance[field.id], 
                                                  '_blank'
                                                )}
                                                className="px-3 py-1 text-sm bg-[#F16745] text-white rounded hover:bg-[#F16745]/90 transition-colors"
                                              >
                                                View File
                                              </button>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="text-gray-900">
                                            {rowInstance?.[field.id] || 'Not provided'}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}

                <div className="mt-8 flex justify-between border-t pt-6">
                  {currentStep > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentStep(prev => prev - 1);
                        scrollToTop();
                      }}
                      className="px-6 py-2.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745] transition duration-150"
                    >
                      ← Previous
                    </button>
                  )}
                  
                  {currentStep < (formData?.form_form_steps.length - 1) && (
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentStep(prev => prev + 1);
                        scrollToTop();
                      }}
                      className="ml-auto px-6 py-2.5 bg-[#F16745] text-sm font-medium rounded-lg text-white hover:bg-[#F16745] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745] transition duration-150"
                    >
                      Next →
                    </button>
                  )}
                </div>
              </div>
            </div>
          </main>

          {isMobileMenuOpen && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default UserSubmissionView;